import React, { useState, useEffect, useRef } from "react";
import { makeStyles } from "@mui/styles";
import { Dialog, IconButton } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import DownloadIcon from "@mui/icons-material/Download";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import VideocamIcon from "@mui/icons-material/Videocam";
import ZoomInIcon from "@mui/icons-material/ZoomIn";
import ZoomOutIcon from "@mui/icons-material/ZoomOut";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import NavigateBeforeIcon from "@mui/icons-material/NavigateBefore";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import RotateRightIcon from "@mui/icons-material/RotateRight";

import api from "../../services/api";

const useStyles = makeStyles(theme => ({
	mediaBox: {
		display: "inline-block",
		width: "fit-content",
		maxWidth: "min(360px, 78vw)",
		"& > div": {
			width: "auto !important",
			maxWidth: "100% !important",
		},
		"& img": {
			display: "block",
			width: "100% !important",
			height: "auto !important",
			maxWidth: "100%",
			borderRadius: 8,
			objectFit: "cover",
		},
		[theme.breakpoints.down("sm")]: {
			maxWidth: "min(300px, 82vw)",
		},
	},
	modalPaper: {
		backgroundColor: "rgba(0,0,0,0.9)",
		boxShadow: "none",
		maxWidth: "none",
	},
	modalImageWrap: {
		position: "relative",
		display: "flex",
		alignItems: "center",
		justifyContent: "center",
		width: "100%",
		height: "100%",
		padding: theme.spacing(2),
		boxSizing: "border-box",
		overflow: "hidden",
	},
	modalImage: {
		maxWidth: "92vw",
		maxHeight: "88vh",
		width: "auto",
		height: "auto",
		objectFit: "contain",
		borderRadius: 6,
		transition: "transform 120ms ease",
		transformOrigin: "center center",
	},
	closeButton: {
		position: "absolute",
		top: theme.spacing(1),
		right: theme.spacing(1),
		color: "#fff",
		backgroundColor: "rgba(0,0,0,0.35)",
		"&:hover": {
			backgroundColor: "rgba(0,0,0,0.55)",
		},
	},
	downloadButton: {
		position: "absolute",
		top: theme.spacing(1),
		right: theme.spacing(7.5),
		color: "#fff",
		backgroundColor: "rgba(0,0,0,0.35)",
		"&:hover": {
			backgroundColor: "rgba(0,0,0,0.55)",
		},
	},
	viewerToolbar: {
		position: "absolute",
		top: theme.spacing(1),
		left: "50%",
		transform: "translateX(-50%)",
		display: "flex",
		alignItems: "center",
		gap: theme.spacing(0.75),
		padding: "4px 8px",
		borderRadius: 999,
		backgroundColor: "rgba(0,0,0,0.38)",
		zIndex: 2,
	},
	toolbarButton: {
		color: "#fff",
		backgroundColor: "rgba(255,255,255,0.08)",
		"&:hover": {
			backgroundColor: "rgba(255,255,255,0.18)",
		},
		"&.Mui-disabled": {
			color: "rgba(255,255,255,0.35)",
		},
	},
	zoomValue: {
		minWidth: 44,
		textAlign: "center",
		color: "#fff",
		fontSize: "0.8rem",
		fontWeight: 600,
		userSelect: "none",
	},
	navButton: {
		position: "absolute",
		top: "50%",
		transform: "translateY(-50%)",
		width: 48,
		height: 48,
		color: "#fff",
		backgroundColor: "rgba(0,0,0,0.34)",
		zIndex: 2,
		"&:hover": {
			backgroundColor: "rgba(0,0,0,0.56)",
		},
		"&.Mui-disabled": {
			display: "none",
		},
	},
	navButtonPrevious: {
		left: theme.spacing(2),
	},
	navButtonNext: {
		right: theme.spacing(2),
	},
	imageCounter: {
		position: "absolute",
		left: "50%",
		bottom: theme.spacing(2),
		transform: "translateX(-50%)",
		padding: "4px 10px",
		borderRadius: 999,
		color: "#fff",
		backgroundColor: "rgba(0,0,0,0.38)",
		fontSize: "0.78rem",
		fontWeight: 600,
		userSelect: "none",
		zIndex: 2,
	},
	messageMedia: {
		width: "100%",
		maxWidth: "100%",
		height: "auto",
		display: "block",
		objectFit: "cover",
		borderTopLeftRadius: 8,
		borderTopRightRadius: 8,
		borderBottomLeftRadius: 8,
		borderBottomRightRadius: 8,
	},
	videoMedia: {
		width: "100%",
		maxWidth: "100%",
		height: "auto",
		display: "block",
		borderTopLeftRadius: 8,
		borderTopRightRadius: 8,
		borderBottomLeftRadius: 8,
		borderBottomRightRadius: 8,
	},
	videoThumbWrap: {
		position: "relative",
		display: "block",
		cursor: "pointer",
		overflow: "hidden",
		borderRadius: 8,
		backgroundColor: "#111b21",
	},
	videoOverlay: {
		position: "absolute",
		inset: 0,
		display: "flex",
		alignItems: "center",
		justifyContent: "center",
		pointerEvents: "none",
		background:
			"linear-gradient(180deg, rgba(0,0,0,0.08) 0%, rgba(0,0,0,0.18) 100%)",
	},
	videoPlayButton: {
		width: 54,
		height: 54,
		borderRadius: "50%",
		display: "flex",
		alignItems: "center",
		justifyContent: "center",
		color: "#fff",
		backgroundColor: "rgba(0,0,0,0.48)",
		boxShadow: "0 2px 10px rgba(0,0,0,0.25)",
		"& svg": {
			fontSize: "2.2rem",
		},
	},
	videoBadge: {
		position: "absolute",
		left: 8,
		bottom: 8,
		display: "inline-flex",
		alignItems: "center",
		gap: 4,
		padding: "3px 7px",
		borderRadius: 999,
		fontSize: "0.72rem",
		fontWeight: 600,
		color: "#fff",
		backgroundColor: "rgba(0,0,0,0.55)",
		pointerEvents: "none",
		"& svg": {
			fontSize: "0.9rem",
		},
	},
	modalVideo: {
		maxWidth: "92vw",
		maxHeight: "88vh",
		width: "auto",
		height: "auto",
		borderRadius: 6,
	},
}));

const getFileNameFromUrl = (url, fallback = "archivo") => {
	if (!url) return fallback;

	try {
		const cleanUrl = url.split("?")[0];
		const fileName = cleanUrl.substring(cleanUrl.lastIndexOf("/") + 1);
		return fileName || fallback;
	} catch (error) {
		return fallback;
	}
};

const clampZoom = value => Math.min(4, Math.max(0.5, value));

const ModalImageCors = ({
	imageUrl,
	mediaType = "image",
	fileName,
	galleryItems = [],
	currentImageId,
}) => {
	const classes = useStyles();
	const [thumbFetching, setThumbFetching] = useState(true);
	const [thumbBlobUrl, setThumbBlobUrl] = useState("");
	const [activeFetching, setActiveFetching] = useState(true);
	const [activeBlobUrl, setActiveBlobUrl] = useState("");
	const [open, setOpen] = useState(false);
	const [activeIndex, setActiveIndex] = useState(0);
	const [zoom, setZoom] = useState(1);
	const [rotation, setRotation] = useState(0);
	const [imageOffset, setImageOffset] = useState({ x: 0, y: 0 });
	const [isDragging, setIsDragging] = useState(false);
	const dragStateRef = useRef({
		pointerId: null,
		startX: 0,
		startY: 0,
		offsetX: 0,
		offsetY: 0,
	});
	const isVideo = mediaType === "video";
	const gallery = !isVideo && Array.isArray(galleryItems) && galleryItems.length > 0
		? galleryItems
		: [{ id: currentImageId || imageUrl, imageUrl, fileName }];
	const initialIndex = Math.max(
		0,
		gallery.findIndex(item =>
			currentImageId ? item.id === currentImageId : item.imageUrl === imageUrl
		)
	);
	const activeItem = gallery[activeIndex] || gallery[initialIndex] || gallery[0] || {};
	const activeImageUrl = activeItem.imageUrl || imageUrl;
	const hasGalleryNavigation = !isVideo && gallery.length > 1;

	useEffect(() => {
		if (!imageUrl || isVideo) return undefined;
		let mounted = true;
		let objectUrl = "";
		const fetchImage = async () => {
			try {
				setThumbFetching(true);
				const { data, headers } = await api.get(imageUrl, {
					responseType: "blob",
				});
				if (!mounted) return;
				objectUrl = window.URL.createObjectURL(
					new Blob([data], { type: headers["content-type"] })
				);
				setThumbBlobUrl(objectUrl);
			} finally {
				if (mounted) setThumbFetching(false);
			}
		};
		fetchImage();

		return () => {
			mounted = false;
			if (objectUrl) {
				window.URL.revokeObjectURL(objectUrl);
			}
		};
	}, [imageUrl, isVideo]);

	useEffect(() => {
		if (!open || !activeImageUrl || isVideo) return undefined;
		let mounted = true;
		let objectUrl = "";
		const fetchActiveImage = async () => {
			try {
				setActiveFetching(true);
				setActiveBlobUrl("");
				const { data, headers } = await api.get(activeImageUrl, {
					responseType: "blob",
				});
				if (!mounted) return;
				objectUrl = window.URL.createObjectURL(
					new Blob([data], { type: headers["content-type"] })
				);
				setActiveBlobUrl(objectUrl);
			} finally {
				if (mounted) setActiveFetching(false);
			}
		};

		fetchActiveImage();

		return () => {
			mounted = false;
			if (objectUrl) {
				window.URL.revokeObjectURL(objectUrl);
			}
		};
	}, [activeImageUrl, isVideo, open]);

	useEffect(() => {
		if (!open) return undefined;

		const handleKeyDown = event => {
			if (event.key === "ArrowLeft" && hasGalleryNavigation) {
				event.preventDefault();
				goToPrevious();
			}
			if (event.key === "ArrowRight" && hasGalleryNavigation) {
				event.preventDefault();
				goToNext();
			}
			if ((event.key === "+" || event.key === "=") && !isVideo) {
				event.preventDefault();
				zoomIn();
			}
			if ((event.key === "-" || event.key === "_") && !isVideo) {
				event.preventDefault();
				zoomOut();
			}
			if (event.key === "0" && !isVideo) {
				event.preventDefault();
				resetZoom();
			}
			if (event.key.toLowerCase() === "r" && !isVideo) {
				event.preventDefault();
				rotateImage();
			}
		};

		window.addEventListener("keydown", handleKeyDown);
		return () => window.removeEventListener("keydown", handleKeyDown);
	});

	const thumbDisplayUrl = thumbFetching ? imageUrl : thumbBlobUrl || imageUrl;
	const activeDisplayUrl =
		activeFetching ? activeImageUrl : activeBlobUrl || activeImageUrl;
	const downloadName =
		activeItem.fileName ||
		fileName ||
		getFileNameFromUrl(activeImageUrl, isVideo ? "video" : "imagen");
	const handleOpen = () => {
		setActiveIndex(initialIndex);
		setZoom(1);
		setRotation(0);
		setImageOffset({ x: 0, y: 0 });
		setOpen(true);
	};
	const handleClose = () => {
		setOpen(false);
		setZoom(1);
		setRotation(0);
		setImageOffset({ x: 0, y: 0 });
		setIsDragging(false);
	};
	const stopPropagation = (event) => event.stopPropagation();
	const zoomIn = () => setZoom(prevZoom => clampZoom(prevZoom + 0.25));
	const zoomOut = () =>
		setZoom(prevZoom => {
			const nextZoom = clampZoom(prevZoom - 0.25);
			if (nextZoom <= 1) {
				setImageOffset({ x: 0, y: 0 });
			}
			return nextZoom;
		});
	const resetZoom = () => {
		setZoom(1);
		setRotation(0);
		setImageOffset({ x: 0, y: 0 });
	};
	const goToPrevious = () => {
		setActiveIndex(prevIndex => (prevIndex - 1 + gallery.length) % gallery.length);
		setZoom(1);
		setRotation(0);
		setImageOffset({ x: 0, y: 0 });
	};
	const goToNext = () => {
		setActiveIndex(prevIndex => (prevIndex + 1) % gallery.length);
		setZoom(1);
		setRotation(0);
		setImageOffset({ x: 0, y: 0 });
	};
	const rotateImage = () => {
		setRotation(prevRotation => (prevRotation + 90) % 360);
		setImageOffset({ x: 0, y: 0 });
	};
	const handleWheel = event => {
		if (isVideo) return;

		event.preventDefault();
		event.stopPropagation();
		setZoom(prevZoom =>
			{
				const nextZoom = clampZoom(prevZoom + (event.deltaY < 0 ? 0.15 : -0.15));
				if (nextZoom <= 1) {
					setImageOffset({ x: 0, y: 0 });
				}
				return nextZoom;
			}
		);
	};
	const handleImagePointerDown = event => {
		stopPropagation(event);
		if (zoom <= 1) return;

		event.preventDefault();
		event.currentTarget.setPointerCapture?.(event.pointerId);
		dragStateRef.current = {
			pointerId: event.pointerId,
			startX: event.clientX,
			startY: event.clientY,
			offsetX: imageOffset.x,
			offsetY: imageOffset.y,
		};
		setIsDragging(true);
	};
	const handleImagePointerMove = event => {
		if (!isDragging || dragStateRef.current.pointerId !== event.pointerId) return;

		event.preventDefault();
		stopPropagation(event);
		const deltaX = event.clientX - dragStateRef.current.startX;
		const deltaY = event.clientY - dragStateRef.current.startY;
		setImageOffset({
			x: dragStateRef.current.offsetX + deltaX,
			y: dragStateRef.current.offsetY + deltaY,
		});
	};
	const handleImagePointerUp = event => {
		if (dragStateRef.current.pointerId !== event.pointerId) return;

		stopPropagation(event);
		event.currentTarget.releasePointerCapture?.(event.pointerId);
		dragStateRef.current.pointerId = null;
		setIsDragging(false);
	};

	return (
		<>
			<div className={classes.mediaBox}>
				{isVideo ? (
					<div className={classes.videoThumbWrap} onClick={handleOpen}>
						<video
							className={classes.videoMedia}
							src={imageUrl}
							preload="metadata"
						/>
						<div className={classes.videoOverlay}>
							<span className={classes.videoPlayButton}>
								<PlayArrowIcon />
							</span>
						</div>
						<span className={classes.videoBadge}>
							<VideocamIcon />
							Video
						</span>
					</div>
				) : (
					<img
						src={thumbDisplayUrl}
						alt="image"
						className={classes.messageMedia}
						onClick={handleOpen}
					/>
				)}
			</div>
			<Dialog
				open={open}
				onClose={handleClose}
				fullScreen
				PaperProps={{ className: classes.modalPaper }}
			>
				<div
					className={classes.modalImageWrap}
					onClick={handleClose}
					onWheel={handleWheel}
				>
					<IconButton
						component="a"
						href={activeDisplayUrl}
						download={downloadName}
						target="_blank"
						rel="noopener noreferrer"
						className={classes.downloadButton}
						size="large"
						onClick={stopPropagation}
					>
						<DownloadIcon />
					</IconButton>
					<IconButton
						onClick={handleClose}
						className={classes.closeButton}
						size="large"
					>
						<CloseIcon />
					</IconButton>
					{!isVideo && (
						<div className={classes.viewerToolbar} onClick={stopPropagation}>
							<IconButton
								className={classes.toolbarButton}
								size="small"
								onClick={zoomOut}
								disabled={zoom <= 0.5}
							>
								<ZoomOutIcon fontSize="small" />
							</IconButton>
							<span className={classes.zoomValue}>
								{Math.round(zoom * 100)}%
							</span>
							<IconButton
								className={classes.toolbarButton}
								size="small"
								onClick={zoomIn}
								disabled={zoom >= 4}
							>
								<ZoomInIcon fontSize="small" />
							</IconButton>
							<IconButton
								className={classes.toolbarButton}
								size="small"
								onClick={resetZoom}
								disabled={zoom === 1 && rotation === 0}
							>
								<RestartAltIcon fontSize="small" />
							</IconButton>
							<IconButton
								className={classes.toolbarButton}
								size="small"
								onClick={rotateImage}
							>
								<RotateRightIcon fontSize="small" />
							</IconButton>
						</div>
					)}
					{hasGalleryNavigation && (
						<>
							<IconButton
								className={`${classes.navButton} ${classes.navButtonPrevious}`}
								onClick={event => {
									stopPropagation(event);
									goToPrevious();
								}}
								size="large"
							>
								<NavigateBeforeIcon />
							</IconButton>
							<IconButton
								className={`${classes.navButton} ${classes.navButtonNext}`}
								onClick={event => {
									stopPropagation(event);
									goToNext();
								}}
								size="large"
							>
								<NavigateNextIcon />
							</IconButton>
							<span className={classes.imageCounter}>
								{activeIndex + 1} / {gallery.length}
							</span>
						</>
					)}
					{isVideo ? (
						<video
							src={imageUrl}
							className={classes.modalVideo}
							controls
							autoPlay
							onClick={stopPropagation}
						/>
					) : (
						<img
							src={activeDisplayUrl}
							alt="image-full"
							className={classes.modalImage}
							style={{
								cursor: zoom > 1 ? (isDragging ? "grabbing" : "grab") : "default",
								touchAction: zoom > 1 ? "none" : "auto",
								transition: isDragging ? "none" : undefined,
								transform: `translate3d(${imageOffset.x}px, ${imageOffset.y}px, 0) rotate(${rotation}deg) scale(${zoom})`,
							}}
							onClick={stopPropagation}
							onPointerDown={handleImagePointerDown}
							onPointerMove={handleImagePointerMove}
							onPointerUp={handleImagePointerUp}
							onPointerCancel={handleImagePointerUp}
						/>
					)}
				</div>
			</Dialog>
		</>
	);
};

export default ModalImageCors;
